import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/NotificationBell";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close the mobile menu whenever the route changes.
  useEffect(() => { setOpen(false); }, [location.pathname, location.hash]);
  // Lock body scroll while menu is open.
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const handleLogout = async () => { await logout(); setOpen(false); navigate("/"); };
  const isActive = (path) => location.pathname === path;

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-[#e2e8f0]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
        <Link to="/" data-testid="logo-link" className="flex items-center flex-shrink-0">
          <div className="font-display font-extrabold text-[#0f1b2d] tracking-tight text-lg">
            Schengen<span className="text-[#2563eb]">File</span>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-7 text-sm">
          <a href="/#how" data-testid="nav-how" className="text-[#0f1b2d] hover:text-[#2563eb] font-medium">How it works</a>
          <Link to="/pricing" data-testid="nav-pricing" className="text-[#0f1b2d] hover:text-[#2563eb] font-medium">Pricing</Link>
          <Link to="/build-your-file" data-testid="nav-build-your-file" className="text-[#0f1b2d] hover:text-[#2563eb] font-medium">Build Your File</Link>
          <div className="relative group" data-testid="nav-travel-guide-dropdown">
            <button className="text-[#0f1b2d] hover:text-[#2563eb] font-medium inline-flex items-center gap-1" data-testid="nav-travel-guide-toggle">
              Travel Guide <ChevronDown className="w-3.5 h-3.5" />
            </button>
            <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition absolute left-1/2 -translate-x-1/2 top-full pt-2 z-40">
              <div className="w-56 rounded-lg border border-[#e2e8f0] bg-white shadow-xl py-2">
                <Link to="/schengen-visa-guide" data-testid="nav-schengen-guide" className="block px-4 py-2 text-sm text-[#0f1b2d] hover:bg-[#f8fafc]">Schengen Visa Guide</Link>
                <Link to="/blog" data-testid="nav-blog" className="block px-4 py-2 text-sm text-[#0f1b2d] hover:bg-[#f8fafc]">Blog</Link>
              </div>
            </div>
          </div>
          <a href="/#faq" data-testid="nav-faq" className="text-[#0f1b2d] hover:text-[#2563eb] font-medium">FAQ</a>
        </nav>

        {/* Right-side actions */}
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <NotificationBell />
          <Link
            to="/apply"
            data-testid="nav-apply-cta"
            className="inline-flex items-center justify-center bg-[#2563eb] hover:bg-[#1d4ed8] text-white text-xs sm:text-sm font-semibold px-3 sm:px-4 py-2 rounded-md transition-all whitespace-nowrap"
          >
            Apply Now <span className="ml-1">→</span>
          </Link>
          {/* Desktop-only Dashboard / Log out / Log in */}
          {user ? (
            <>
              <NavLink
                to={user.role === "admin" ? "/admin" : "/dashboard"}
                data-testid="nav-dashboard"
                className="hidden md:inline text-sm font-medium text-[#0f1b2d] hover:text-[#2563eb]"
              >
                {user.role === "admin" ? "Admin" : "Dashboard"}
              </NavLink>
              <button
                data-testid="logout-btn"
                onClick={handleLogout}
                className="hidden md:inline text-sm font-medium text-[#475569] hover:text-[#0f1b2d]"
              >
                Log out
              </button>
            </>
          ) : (
            <Link to="/login" data-testid="nav-login" className="hidden md:inline text-sm font-medium text-[#0f1b2d] hover:text-[#2563eb]">
              Log in
            </Link>
          )}

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-btn"
            className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-md text-[#0f1b2d] hover:bg-[#f1f5f9]"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Mobile slide-in menu — rendered in a portal: the header's backdrop-blur
          creates a containing block that would otherwise clip this fixed panel. */}
      {open && createPortal(
        <div
          data-testid="mobile-menu-panel"
          className="md:hidden fixed inset-0 z-[100] bg-[#0f1b2d] text-white flex flex-col mobile-menu-enter"
        >
          <div className="flex items-center justify-between px-4 h-16 border-b border-white/10 flex-shrink-0">
            <div className="font-display font-extrabold text-lg">
              Schengen<span className="text-[#60a5fa]">File</span>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              data-testid="mobile-menu-close"
              className="inline-flex items-center justify-center w-10 h-10 rounded-md text-white hover:bg-white/10"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto px-6 py-6 text-lg font-semibold" data-testid="mobile-menu-links">
            <a href="/#how" onClick={() => setOpen(false)} data-testid="mobile-nav-how" className="flex items-center min-h-[48px] py-2 border-b border-white/10">How it works</a>
            <Link to="/pricing" onClick={() => setOpen(false)} data-testid="mobile-nav-pricing" className={`flex items-center min-h-[48px] py-2 border-b border-white/10 ${isActive("/pricing") ? "text-[#60a5fa] font-bold" : ""}`}>Pricing</Link>
            <Link to="/build-your-file" onClick={() => setOpen(false)} data-testid="mobile-nav-build-your-file" className={`flex items-center min-h-[48px] py-2 border-b border-white/10 ${isActive("/build-your-file") ? "text-[#60a5fa] font-bold" : ""}`}>Build Your File</Link>
            <Link to="/schengen-visa-guide" onClick={() => setOpen(false)} data-testid="mobile-nav-schengen-guide" className={`flex items-center min-h-[48px] py-2 border-b border-white/10 ${isActive("/schengen-visa-guide") ? "text-[#60a5fa] font-bold" : ""}`}>Schengen Visa Guide</Link>
            <Link to="/blog" onClick={() => setOpen(false)} data-testid="mobile-nav-blog" className={`flex items-center min-h-[48px] py-2 border-b border-white/10 ${isActive("/blog") ? "text-[#60a5fa] font-bold" : ""}`}>Blog</Link>
            <a href="/#faq" onClick={() => setOpen(false)} data-testid="mobile-nav-faq" className="flex items-center min-h-[48px] py-2 border-b border-white/10">FAQ</a>

            <div className="my-5 border-t border-white/20" aria-hidden />

            <Link
              to="/apply"
              data-testid="mobile-apply-cta"
              onClick={() => setOpen(false)}
              className="flex w-full min-h-[48px] items-center justify-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold px-4 py-3 rounded-md"
            >
              Apply Now <ArrowRight className="w-4 h-4" />
            </Link>

            {user ? (
              <>
                <Link
                  to={user.role === "admin" ? "/admin" : "/dashboard"}
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-dashboard"
                  className={`mt-4 flex items-center min-h-[48px] py-2 border-b border-white/10 ${isActive(user.role === "admin" ? "/admin" : "/dashboard") ? "text-[#60a5fa] font-bold" : ""}`}
                >
                  {user.role === "admin" ? "Admin" : "Dashboard"}
                </Link>
                <button type="button" onClick={handleLogout} data-testid="mobile-logout-btn" className="flex items-center w-full text-left min-h-[48px] py-2 border-b border-white/10 text-white/80">
                  Log out
                </button>
              </>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} data-testid="mobile-nav-login" className={`mt-4 flex items-center min-h-[48px] py-2 border-b border-white/10 ${isActive("/login") ? "text-[#60a5fa] font-bold" : ""}`}>
                Log in
              </Link>
            )}
          </nav>
        </div>,
        document.body
      )}
    </header>
  );
}
